import React from 'react';
import { liveQuery, Subscription } from 'dexie';
import './global_notification_coordinator.css';
import { db } from '../../../myt_chat/chat/database/MytChatDB';
import CareTicket from '../../../myt_chat/chat/models/CareTicket';
import UserRole from '../../../myt_chat/chat/enums/UserRole';
import MytNotification from '../../../myt_chat/notification';
import { getAuthUserInfoOrDefault } from '../../../myt_chat/network/cookie';

type ChatNotificationCoordinatorState = {
  activeChannelUuid: string | null;
  toastMessage: string;
};

const getChatRoute = (role: UserRole) => {
  return role === UserRole.TEACHER ? '/teacher_chat' : '/student_chat';
};

class GlobalChatNotificationCoordinator extends React.Component<
  Record<string, never>,
  ChatNotificationCoordinatorState
> {
  private ticketSubscription?: Subscription;
  private previousTickets = new Map<string, CareTicket>();
  private hasHydratedInitialTickets = false;
  private toastTimer: number | null = null;

  state: ChatNotificationCoordinatorState = {
    activeChannelUuid: null,
    toastMessage: ''
  };

  componentDidMount() {
    const authInfo = getAuthUserInfoOrDefault();

    if (authInfo.role !== UserRole.UNKNOWN) {
      MytNotification.webPushInit();
    }

    this.ticketSubscription = liveQuery(() => db.ticket.toArray()).subscribe(
      (tickets) => {
        this.syncTickets(tickets);
      },
      (error) => {
        console.log('Error in global chat notification subscription', error);
      }
    );

    window.addEventListener('chat:open', this.handleChatOpen as EventListener);
    window.addEventListener('chat:closed', this.handleChatClosed as EventListener);
  }

  componentWillUnmount() {
    this.ticketSubscription?.unsubscribe();
    this.ticketSubscription = undefined;

    window.removeEventListener('chat:open', this.handleChatOpen as EventListener);
    window.removeEventListener('chat:closed', this.handleChatClosed as EventListener);

    if (this.toastTimer) {
      window.clearTimeout(this.toastTimer);
      this.toastTimer = null;
    }
  }

  private syncTickets = (tickets: CareTicket[]) => {
    const nextTickets = new Map(tickets.map((ticket) => [ticket.user, { ...ticket }]));

    if (!this.hasHydratedInitialTickets) {
      this.previousTickets = nextTickets;
      this.hasHydratedInitialTickets = true;
      return;
    }

    let notificationTicket: CareTicket | null = null;

    for (const ticket of tickets) {
      const previous = this.previousTickets.get(ticket.user);
      const isActiveChat = this.state.activeChannelUuid === ticket.user;
      const unreadIncreased = ticket.unread > (previous?.unread || 0);
      const messageChanged = !!ticket.message?.id && ticket.message.id !== previous?.message?.id;

      if (
        !notificationTicket &&
        !isActiveChat &&
        ticket.unread > 0 &&
        (unreadIncreased || messageChanged)
      ) {
        notificationTicket = ticket;
      }
    }

    this.previousTickets = nextTickets;

    if (notificationTicket) {
      window.setTimeout(() => this.notifyForTicket(notificationTicket!), 0);
    }
  };

  private notifyForTicket = (ticket: CareTicket) => {
    const { name, message } = ticket;
    const shown = MytNotification.showLocalNotification(
      ticket.name,
      `New message from ${name}: ${message.text || 'You have a new message'}`,
      () => {
        this.openTicket(ticket);
      }
    );

    if (!shown && typeof document !== 'undefined' && !document.hidden) {
      this.showToast(`New message from ${name}`);
    }
  };

  private openTicket = (ticket: CareTicket) => {
    const redirectUrl = getChatRoute(ticket.role);

    try {
      sessionStorage.setItem(
        'pendingChatOpen',
        JSON.stringify({
          role: ticket.role === UserRole.TEACHER ? 'teacher' : 'student',
          channelUuid: ticket.user,
          ts: Date.now()
        })
      );
    } catch (error) {
      console.log('Error setting pendingChatOpen from notification click', error);
    }

    if (!window.location.pathname.startsWith(redirectUrl)) {
      const qp = `?from=push&channel=${encodeURIComponent(ticket.user)}`;
      window.location.assign(`${redirectUrl}${qp}`);
      return;
    }

    window.dispatchEvent(new CustomEvent('chat:open', { detail: { channelUuid: ticket.user } }));
  };

  private showToast = (message: string) => {
    if (this.toastTimer) {
      window.clearTimeout(this.toastTimer);
    }

    this.setState({ toastMessage: message });
    this.toastTimer = window.setTimeout(() => {
      this.setState({ toastMessage: '' });
      this.toastTimer = null;
    }, 4000);
  };

  private handleChatOpen = (event: Event) => {
    const customEvent = event as CustomEvent<{ channelUuid?: string; userUuid?: string }>;
    const nextActive = customEvent.detail?.userUuid || customEvent.detail?.channelUuid || null;

    if (nextActive && nextActive !== this.state.activeChannelUuid) {
      this.setState({ activeChannelUuid: nextActive });
    }
  };

  private handleChatClosed = (event: Event) => {
    const customEvent = event as CustomEvent<{ channelUuid?: string; userUuid?: string }>;
    const closedChannel = customEvent.detail?.userUuid || customEvent.detail?.channelUuid || null;

    if (closedChannel && closedChannel === this.state.activeChannelUuid) {
      this.setState({ activeChannelUuid: null });
    }
  };

  render() {
    if (!this.state.toastMessage) {
      return null;
    }

    return <div className="chat-global-toast">{this.state.toastMessage}</div>;
  }
}

export default GlobalChatNotificationCoordinator;
